import React, { useState, useCallback } from "react";
import { Mail, MessageSquare, Bell, Check, AlertTriangle, CircleSlash, Loader2, RotateCcw, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import EmailViewerModal from "@/components/admin/EmailViewerModal";

/**
 * Iter P + Q — Notification History timeline with per-recipient retry.
 *
 * Data source: `order.admin_notifications.<event>.providers.<name>.recipients[].attempts[]`
 * populated by the backend NotificationDispatcher + retry service.
 *
 * Retry gating (frontend):
 *   - Button visible only when latest attempt status is retry-eligible.
 *   - Hidden for sent / skipped_disabled / skipped_not_configured / removed.
 *   - Disabled while a request is in flight (prevents double-click storms).
 *
 * Retry gating (backend):
 *   - Recipient MUST match the audit trail (blocks injection).
 *   - Content is re-rendered from the order — no caller subject/body.
 *   - Atomic lock + 5s cool-down enforced server-side.
 */

const EVENT_LABEL = {
  payment_submitted: "Payment Submitted",
  // Phase 1-ops · Customer-lifecycle emails are now recorded in
  // Notification History alongside admin notifications.
  payment_verified:  "Payment Verified",
  preparing:         "Order Preparing",
  ready:             "Ready",
  shipped:           "Shipped",
  completed:         "Order Complete",
};
const PROVIDER_LABEL = { email: "Admin Email", whatsapp: "WhatsApp" };
const PROVIDER_ICON = { email: Mail, whatsapp: MessageSquare };

const STATUS_META = {
  sent:                       { label: "Sent",                     tone: "ok",   Icon: Check },
  partial:                    { label: "Partial delivery",         tone: "warn", Icon: AlertTriangle },
  failed:                     { label: "Failed",                   tone: "err",  Icon: AlertTriangle },
  skipped_disabled:           { label: "Skipped — disabled",       tone: "mute", Icon: CircleSlash },
  skipped_not_configured:     { label: "Skipped — not configured", tone: "mute", Icon: CircleSlash },
};

// Client-side allowlist mirrors the server's RETRYABLE_STATUSES set. Kept
// deliberately conservative — if the server tightens, the request 409s and
// the toast surfaces the reason.
const RETRYABLE_STATUSES = new Set(["failed", "partial"]);

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function toneClasses(tone) {
  switch (tone) {
    case "ok":   return "border-emerald-700 bg-emerald-50 text-emerald-900";
    case "warn": return "border-amber-700 bg-amber-50 text-amber-900";
    case "err":  return "border-red-700 bg-red-50 text-red-900";
    default:     return "border-neutral-400 bg-neutral-50 text-neutral-700";
  }
}

// One row per attempt on a recipient. Renders attempt number so operators
// can visually walk the ladder from #1 to #N.
function AttemptRow({ attempt, isLast }) {
  const meta = STATUS_META[attempt.status] || { label: attempt.status, tone: "mute", Icon: Bell };
  const StatusIcon = meta.Icon;
  const number = attempt.attempt || 1;
  const label = number === 1 ? "Initial attempt" : `Retry attempt #${number - 1}`;
  return (
    <div className="ml-2 flex items-start gap-2 text-[11px]" data-testid={`notif-attempt-${number}`}>
      <span
        className={`inline-flex shrink-0 items-center gap-1 border rounded-full px-1.5 py-0.5 font-bold ${toneClasses(meta.tone)}`}
      >
        <StatusIcon size={10} /> {meta.label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[var(--ink)]">
          <span className="font-bold">{label}</span>
          <span className="text-[var(--smoke)]"> — {formatTs(attempt.completed_at || attempt.requested_at)}</span>
        </div>
        {attempt.error && (
          <div className="text-[var(--smoke)] italic truncate" title={attempt.error}>
            {attempt.error}
          </div>
        )}
        {attempt.reason && attempt.reason !== attempt.error && (
          <div className="text-[var(--smoke)]">Reason: {attempt.reason}</div>
        )}
      </div>
    </div>
  );
}

function RecipientRow({ providerKey, recipient, orderId, event, onRetried, setViewingMessageId }) {
  const attempts = Array.isArray(recipient.attempts) && recipient.attempts.length
    ? recipient.attempts
    // Backwards-compat: pre-Iter-Q orders have no attempts[]. Synthesize an
    // attempt #1 from the top-level status/detail so the timeline still renders.
    : [{
        attempt: 1,
        status: recipient.status,
        completed_at: recipient.at,
        error: recipient.status === "sent" ? null : (recipient.detail || null),
        initiated_by: "system:auto",
      }];
  const effectiveStatus = attempts[attempts.length - 1]?.status || recipient.status;
  const meta = STATUS_META[effectiveStatus] || { label: effectiveStatus, tone: "mute", Icon: Bell };
  const StatusIcon = meta.Icon;
  const canRetry = RETRYABLE_STATUSES.has(effectiveStatus);

  const [busy, setBusy] = useState(false);
  const handleRetry = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${orderId}/notifications/retry`, {
        event,
        provider: providerKey,
        recipient: recipient.address,
        reason: "Manual retry from admin timeline",
      });
      toast.success(`Resent notification to ${recipient.address}`);
      if (onRetried) await onRetried();
    } catch (e) {
      const msg = formatApiError(e) || "Retry failed.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }, [busy, orderId, event, providerKey, recipient.address, onRetried]);

  return (
    <li className="border border-neutral-200 rounded-lg bg-white p-2"
        data-testid={`notif-recipient-${providerKey}-${recipient.address}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={`inline-flex shrink-0 items-center gap-1 border rounded-full px-1.5 py-0.5 text-[10px] font-bold ${toneClasses(meta.tone)}`}
            data-testid={`notif-recipient-status-${providerKey}-${recipient.address}`}
          >
            <StatusIcon size={10} /> {meta.label}
          </span>
          <span className="font-mono text-xs text-[var(--ink)] break-all">{recipient.address}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter S · Stage 3 — View Email opens a read-only snapshot of the
              exact email that was dispatched. Only shown for email
              providers with a provider_message_id we can look up. */}
          {providerKey === "email" && recipient.provider_message_id && (
            <button
              type="button"
              onClick={() => setViewingMessageId(recipient.provider_message_id)}
              className="inline-flex items-center gap-1 border-2 border-[var(--ink)] rounded-full px-2.5 py-1 text-[11px] font-bold bg-white hover:bg-[var(--panini-yellow-soft)] transition"
              data-testid={`notif-view-email-${providerKey}-${recipient.address}`}
              aria-label="View the exact email that was sent"
            >
              <Eye size={11} /> View Email
            </button>
          )}
          {canRetry && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={busy}
              className="inline-flex items-center gap-1 border-2 border-[var(--ink)] rounded-full px-2.5 py-1 text-[11px] font-bold bg-[var(--panini-yellow)] hover:bg-yellow-300 disabled:opacity-60 disabled:cursor-not-allowed transition"
              data-testid={`notif-retry-${providerKey}-${recipient.address}`}
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
              {busy ? "Resending…" : "Resend to this recipient"}
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 space-y-1">
        {attempts.map((a, i) => (
          <AttemptRow key={`att-${a.attempt}-${i}`} attempt={a} isLast={i === attempts.length - 1} />
        ))}
      </div>
    </li>
  );
}

// Phase 2 · Customer-facing lifecycle events display "Customer Email"
// instead of the generic provider label. Provider key + database schema
// remain unchanged — this is display copy only.
const _CUSTOMER_LIFECYCLE_EVENTS = new Set([
  "payment_verified", "preparing", "ready", "shipped", "completed",
]);

function ProviderBlock({ providerKey, entry, orderId, event, onRetried, setViewingMessageId }) {
  const Icon = PROVIDER_ICON[providerKey] || Bell;
  const isCustomerLifecycle = providerKey === "email" && _CUSTOMER_LIFECYCLE_EVENTS.has(event);
  const label = isCustomerLifecycle ? "Customer Email" : (PROVIDER_LABEL[providerKey] || providerKey);
  const meta = STATUS_META[entry.status] || { label: entry.status, tone: "mute", Icon: Bell };
  const StatusIcon = meta.Icon;
  const recipients = Array.isArray(entry.recipients) ? entry.recipients : [];

  return (
    <div className="pl-6 relative py-3" data-testid={`notif-provider-${providerKey}`}>
      <span className="absolute left-2 top-0 bottom-0 w-px bg-neutral-300" aria-hidden />
      <span className="absolute left-1 top-4 w-2 h-2 rounded-full bg-[var(--ink)]" aria-hidden />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon size={14} className="text-[var(--ink)]" />
          <span className="font-bold text-sm text-[var(--ink)]">{label}</span>
          <span
            className={`inline-flex items-center gap-1 border-2 rounded-full px-2 py-0.5 text-[11px] font-bold ${toneClasses(meta.tone)}`}
            data-testid={`notif-status-${providerKey}`}
          >
            <StatusIcon size={11} /> {meta.label}
          </span>
        </div>
        <div className="text-xs text-[var(--smoke)]" data-testid={`notif-timestamp-${providerKey}`}>
          {formatTs(entry.at)}
        </div>
      </div>
      {entry.detail && <div className="text-xs text-[var(--smoke)] mt-1">{entry.detail}</div>}
      {recipients.length > 0 && (
        <ul className="mt-2 space-y-2" data-testid={`notif-recipients-${providerKey}`}>
          {recipients.map((r, i) => (
            <RecipientRow
              key={`${r.address}-${i}`}
              providerKey={providerKey}
              recipient={r}
              orderId={orderId}
              event={event}
              onRetried={onRetried}
              setViewingMessageId={setViewingMessageId}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function NotificationHistoryCard({ order, onRefresh }) {
  const events = order?.admin_notifications || {};
  const eventKeys = Object.keys(events);
  const hasHistory = eventKeys.length > 0;
  const orderId = order?.public_id;
  // Iter S · Stage 3 — one modal, shared across all recipients. Null when
  // closed; the provider_message_id string is set by the "View Email"
  // buttons rendered inside RecipientRow.
  const [viewingMessageId, setViewingMessageId] = useState(null);

  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5 shadow-[6px_6px_0_var(--ink)]"
         data-testid="notification-history-card">
      <div className="flex items-center gap-2 mb-3">
        <Bell size={18} className="text-[var(--panini-red)]" />
        <h3 className="font-display text-lg text-[var(--ink)]">Notification History</h3>
      </div>
      <p className="text-xs text-[var(--smoke)] mb-4">
        Chronological record of every admin alert dispatched for this order — plus any manual retry
        attempts. Data flows directly from the notification dispatcher&apos;s audit trail.
      </p>

      {!hasHistory ? (
        <div className="py-6 text-center text-sm text-[var(--smoke)]" data-testid="notif-empty">
          <CircleSlash size={20} className="mx-auto mb-2 opacity-60" />
          No admin notifications have been dispatched for this order yet.
        </div>
      ) : (
        <div className="space-y-4">
          {eventKeys.map((key) => {
            const ev = events[key] || {};
            const providers = ev.providers || {};
            const providerKeys = Object.keys(providers);
            return (
              <div key={key} className="border-l-2 border-[var(--panini-red)] pl-3" data-testid={`notif-event-${key}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-[var(--panini-red)]" aria-hidden />
                  <span className="font-bold text-sm text-[var(--ink)]">
                    {EVENT_LABEL[key] || key}
                  </span>
                  <span className="text-xs text-[var(--smoke)]" data-testid={`notif-event-ts-${key}`}>
                    {formatTs(ev.dispatched_at || ev.locked_at)}
                  </span>
                </div>
                {providerKeys.length === 0 ? (
                  <div className="pl-4 text-xs text-[var(--smoke)] italic">
                    <Loader2 size={12} className="inline animate-spin mr-1" />
                    Dispatch in progress…
                  </div>
                ) : (
                  providerKeys.map((pk) => (
                    <ProviderBlock
                      key={pk}
                      providerKey={pk}
                      entry={providers[pk] || {}}
                      orderId={orderId}
                      event={key}
                      onRetried={onRefresh}
                      setViewingMessageId={setViewingMessageId}
                    />
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Iter S · Stage 3 — shared read-only email viewer. */}
      {viewingMessageId && (
        <EmailViewerModal
          providerMessageId={viewingMessageId}
          onClose={() => setViewingMessageId(null)}
        />
      )}
    </div>
  );
}
